import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ArrowUpRight, ArrowDownRight, Minus, ChevronDown, ChevronUp, Edit2, UserCheck, RefreshCw, Copy, Loader2, Eye, Zap, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type MyLoadRow, type LoadStatus } from "@/hooks/useMyLoads";
import { useToast } from "@/hooks/use-toast";
import { LoadDetailsPanel } from "@/components/search/LoadDetailsPanel";
import { LoadViewersDialog } from "@/components/dashboard/LoadViewersDialog";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyCredits } from "@/hooks/useCompanyCredits";

const FILTER_TABS = ["All", "Active", "Expired", "Updates"] as const;
const STATE_CHIPS = ["TX", "NY", "CA", "FL", "IL"];

type MyLoadSortOption =
  | "ref_az" | "ref_za"
  | "status_az" | "status_za"
  | "origin_az" | "origin_za"
  | "dest_az" | "dest_za"
  | "miles_asc" | "miles_desc"
  | "equip_az" | "equip_za"
  | "rate_asc" | "rate_desc"
  | "ai_rate_asc" | "ai_rate_desc"
  | "mkt_rpm_asc" | "mkt_rpm_desc"
  | "age_newest" | "age_oldest"
  | "views_asc" | "views_desc";

type SortHeaderKey = "ref" | "status" | "origin" | "dest" | "miles" | "equip" | "rate" | "ai_rate" | "mkt_rpm" | "age" | "views";

const HEADER_SORT_MAP: Record<SortHeaderKey, [MyLoadSortOption, MyLoadSortOption]> = {
  ref:      ["ref_az", "ref_za"],
  status:   ["status_az", "status_za"],
  origin:   ["origin_az", "origin_za"],
  dest:     ["dest_az", "dest_za"],
  miles:    ["miles_asc", "miles_desc"],
  equip:    ["equip_az", "equip_za"],
  rate:     ["rate_desc", "rate_asc"],
  ai_rate:  ["ai_rate_desc", "ai_rate_asc"],
  mkt_rpm:  ["mkt_rpm_desc", "mkt_rpm_asc"],
  age:      ["age_newest", "age_oldest"],
  views:    ["views_desc", "views_asc"],
};

function SortableTH({ label, headerKey, sortBy, onSort }: {
  label: string;
  headerKey: SortHeaderKey;
  sortBy: MyLoadSortOption;
  onSort: (s: MyLoadSortOption) => void;
}) {
  const [primary, reverse] = HEADER_SORT_MAP[headerKey];
  const isActive = sortBy === primary || sortBy === reverse;
  const isReversed = sortBy === reverse;

  const handleClick = () => {
    if (!isActive) onSort(primary);
    else onSort(isReversed ? primary : reverse);
  };

  return (
    <th
      onClick={handleClick}
      className={cn(
        "px-3 py-1.5 text-left text-[10px] font-bold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none transition-colors hover:text-foreground",
        isActive ? "text-foreground" : "text-muted-foreground"
      )}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {isActive && (isReversed
          ? <ChevronUp className="h-2.5 w-2.5" />
          : <ChevronDown className="h-2.5 w-2.5" />
        )}
      </span>
    </th>
  );
}

function sortLoads(loads: MyLoadRow[], sortBy: MyLoadSortOption, viewCounts: Record<string, number>): MyLoadRow[] {
  const arr = [...loads];
  const cmp = (a: number, b: number) => a - b;
  const strCmp = (a: string, b: string) => a.localeCompare(b);
  switch (sortBy) {
    case "ref_az":       return arr.sort((a, b) => strCmp(a.ref, b.ref));
    case "ref_za":       return arr.sort((a, b) => strCmp(b.ref, a.ref));
    case "status_az":    return arr.sort((a, b) => strCmp(a.status, b.status));
    case "status_za":    return arr.sort((a, b) => strCmp(b.status, a.status));
    case "origin_az":    return arr.sort((a, b) => strCmp(a.origin, b.origin));
    case "origin_za":    return arr.sort((a, b) => strCmp(b.origin, a.origin));
    case "dest_az":      return arr.sort((a, b) => strCmp(a.destination, b.destination));
    case "dest_za":      return arr.sort((a, b) => strCmp(b.destination, a.destination));
    case "miles_asc":    return arr.sort((a, b) => cmp(a.miles, b.miles));
    case "miles_desc":   return arr.sort((a, b) => cmp(b.miles, a.miles));
    case "equip_az":     return arr.sort((a, b) => strCmp(a.equipment, b.equipment));
    case "equip_za":     return arr.sort((a, b) => strCmp(b.equipment, a.equipment));
    case "rate_asc":     return arr.sort((a, b) => cmp(a.rate, b.rate));
    case "rate_desc":    return arr.sort((a, b) => cmp(b.rate, a.rate));
    case "ai_rate_asc":  return arr.sort((a, b) => cmp(a.aiRate ?? 0, b.aiRate ?? 0));
    case "ai_rate_desc": return arr.sort((a, b) => cmp(b.aiRate ?? 0, a.aiRate ?? 0));
    case "mkt_rpm_asc":  return arr.sort((a, b) => cmp(a.marketRpm, b.marketRpm));
    case "mkt_rpm_desc": return arr.sort((a, b) => cmp(b.marketRpm, a.marketRpm));
    case "age_newest":   return arr.sort((a, b) => cmp(a.ageHours, b.ageHours));
    case "age_oldest":   return arr.sort((a, b) => cmp(b.ageHours, a.ageHours));
    case "views_asc":    return arr.sort((a, b) => cmp(viewCounts[a.id] ?? 0, viewCounts[b.id] ?? 0));
    case "views_desc":   return arr.sort((a, b) => cmp(viewCounts[b.id] ?? 0, viewCounts[a.id] ?? 0));
    default:             return arr;
  }
}
const BOOST_TIERS = [
  { minutes: 10, credits: 10 },
  { minutes: 20, credits: 19 },
  { minutes: 30, credits: 27 },
];

const STATUS_STYLES: Record<LoadStatus, { label: string; className: string }> = {
  active:  { label: "Active",  className: "bg-primary/10 text-primary border-primary/20" },
  offered: { label: "Offered", className: "bg-info-subtle text-info border-info/20" },
  expired: { label: "Expired", className: "bg-muted text-muted-foreground border-border" },
  updates: { label: "Updates", className: "bg-warning-subtle text-warning border-warning/20" },
};

function ageLabel(h: number) {
  if (h < 1)  return "<1h";
  if (h < 24) return `${h}h`;
  return `${Math.floor(h/24)}d ${h%24}h`;
}

interface LoadsTableV2Props {
  loads: MyLoadRow[];
  loading: boolean;
  refetch: () => void;
  maxRows?: number;
  viewCounts?: Record<string, number>;
}

export function LoadsTableV2({ loads, loading, refetch, maxRows, viewCounts = {} }: LoadsTableV2Props) {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [activeStates, setActiveStates] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<MyLoadSortOption>("age_newest");
  const [expandedLoadId, setExpandedLoadId] = useState<string | null>(null);
  const [viewerLoadId, setViewerLoadId] = useState<string | null>(null);
  const viewerLoad = viewerLoadId ? loads.find(l => l.id === viewerLoadId) : null;
  const { wallet, isCompanyAdmin } = useCompanyCredits();

  // Fetch active boosts for user's loads
  const [activeBoosts, setActiveBoosts] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!loads.length) return;
    const loadIds = loads.map(l => l.id);
    supabase
      .from('load_boosts')
      .select('load_id, expires_at')
      .in('load_id', loadIds.slice(0, 100))
      .eq('status', 'active')
      .gt('expires_at', new Date().toISOString())
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        for (const row of data) map[row.load_id] = row.expires_at;
        setActiveBoosts(map);
      });
  }, [loads]);

  const [boostingLoadId, setBoostingLoadId] = useState<string | null>(null);
  

  const REMOVE_REASONS = [
    { value: 'covered', label: 'Covered', description: 'Load was filled / covered' },
    { value: 'cancelled', label: 'Cancelled', description: 'Shipper cancelled the load' },
    { value: 'unposted', label: 'Unposted', description: 'Temporarily removed from board' },
    { value: 'deleted', label: 'Deleted', description: 'Remove permanently from My Loads' },
  ] as const;

  const handleRemoveWithReason = async (loadId: string, reason: string) => {
    try {
      const { error } = await supabase
        .from('loads')
        .update({ status: reason as any })
        .eq('id', loadId);
      if (error) throw error;
      const label = REMOVE_REASONS.find(r => r.value === reason)?.label ?? reason;
      toast({ title: "Load removed", description: `The load has been marked as ${label} and removed from the board.` });
      refetch();
    } catch (err: any) {
      toast({ title: "Failed to remove load", description: err.message, variant: "destructive" });
    }
  };
  const handleBoost = async (loadId: string, minutes: number, credits: number) => {
    if (!user) return;
    setBoostingLoadId(null);
    try {
      const { data, error } = await supabase.rpc('boost_load', {
        p_load_id: loadId,
        p_duration_minutes: minutes,
        p_credits: credits,
      });
      if (error) throw error;
      // Check application-level error from the RPC jsonb response
      if (data && typeof data === 'object' && (data as any).success === false) {
        toast({ title: "Boost failed", description: (data as any).error || "Unknown error", variant: "destructive" });
        return;
      }
      toast({ title: "Load boosted!", description: `Boosted for ${minutes} minutes (${credits} credits)` });
      refetch();
    } catch (err: any) {
      toast({ title: "Boost failed", description: err.message, variant: "destructive" });
    }
  };

  // Derive unique origin states for chip filtering
  const stateSet = new Set(loads.map(l => l.originState));
  const availableChips = STATE_CHIPS.filter(s => stateSet.has(s));

  let filtered = loads.filter(l => {
    if (activeFilter !== "All" && l.status !== activeFilter.toLowerCase()) return false;
    if (activeStates.length > 0 && !activeStates.includes(l.originState)) return false;
    return true;
  });

  filtered = sortLoads(filtered, sortBy, viewCounts);

  const displayed = maxRows ? filtered.slice(0, maxRows) : filtered;

  const updatesCount = loads.filter(l => l.status === 'updates').length;

  const handleEdit = (loadId: string) => navigate(`/post-load?edit=${loadId}`);
  const handleRepost = (loadId: string) => navigate(`/post-load?repost=${loadId}`);
  const handleDuplicate = (loadId: string) => navigate(`/post-load?duplicate=${loadId}`);

  return (
    <>
      <div className="data-table flex flex-col h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
        {/* Filter tab bar */}
        <div className="flex items-center gap-0 border-b bg-muted/40 px-3 pt-1.5">
          {FILTER_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveFilter(tab)}
              className={cn(
                "relative px-3 py-1.5 text-[11px] font-semibold transition-colors border-b-2 -mb-px",
                activeFilter === tab
                  ? "border-accent text-accent"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {tab}
              {tab === "Updates" && updatesCount > 0 && (
                <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger text-[9px] font-bold text-white px-1">{updatesCount}</span>
              )}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1 pb-2">
            {availableChips.map(s => (
              <button
                key={s}
                onClick={() => setActiveStates(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                className={cn(
                  "rounded-full px-2 py-0.5 text-[10px] font-semibold border transition-colors",
                  activeStates.includes(s)
                    ? "bg-accent text-accent-foreground border-accent"
                    : "border-border text-muted-foreground hover:border-accent hover:text-foreground"
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table (desktop) / Cards (mobile) */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground gap-2 flex-1">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-[11px]">Loading loads…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground flex-1">
            <span className="text-[11px]">No loads found. Post a load to get started.</span>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="lg:hidden flex-1 overflow-y-auto p-2 space-y-2">
              {displayed.map((row, i) => {
                const isExpanded = expandedLoadId === row.id;
                const statusStyle = STATUS_STYLES[row.status];
                const hasBoosted = !!activeBoosts[row.id];
                const canBoost = row.status !== 'expired' && !hasBoosted;
                const detailRow = {
                  id: i, loadId: row.id, ageMinutes: row.ageHours * 60,
                  pickupDate: row.pickupDate, dropDate: row.deliveryDate,
                  originCity: row.origin.split(', ')[0] || '', originState: row.originState,
                  destCity: row.destination.split(', ')[0] || '', destState: row.destinationState,
                  pickups: 1, drops: 1, type: 'FTL' as const,
                  rate: row.rate, weight: row.weightLbs, miles: row.miles, equipment: row.equipment,
                  ratePerMile: row.miles > 0 ? Math.round((row.rate / row.miles) * 100) / 100 : 0,
                  company: '', verified: false, createdBy: row.createdBy, contactName: row.contactName,
                  lengthFt: row.lengthFt, commodity: row.commodity, paymentTerms: row.paymentTerms,
                  specialInstructions: row.specialInstructions, referenceNumber: row.referenceNumber,
                  loadNumber: row.loadNumber, pieces: row.pieces,
                  briefAudioPath: row.briefAudioPath, briefDurationSeconds: row.briefDurationSeconds,
                };
                return (
                  <div key={row.id} className={cn("rounded-lg border bg-card overflow-hidden", isExpanded && "border-accent ring-1 ring-accent/30")}>
                    <button
                      onClick={() => setExpandedLoadId(prev => prev === row.id ? null : row.id)}
                      className="w-full text-left p-3 space-y-2 hover:bg-muted/30 transition-colors"
                    >
                      {/* Top: ref + status + age */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-mono font-semibold text-[12px] text-foreground truncate">{row.ref}</span>
                          {hasBoosted && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-brand-teal bg-brand-teal/10 rounded-full px-1.5 py-0.5">
                              <Zap className="h-2.5 w-2.5" />
                              {Math.max(0, Math.ceil((new Date(activeBoosts[row.id]).getTime() - Date.now()) / 60000))}m
                            </span>
                          )}
                          <span className={cn("rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide", statusStyle.className)}>
                            {statusStyle.label}
                          </span>
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{ageLabel(row.ageHours)}</span>
                      </div>

                      {/* Lane */}
                      <div className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
                        <span className="truncate">{row.origin}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="truncate">{row.destination}</span>
                      </div>

                      {/* Meta grid */}
                      <div className="grid grid-cols-3 gap-2 text-[11px]">
                        <div>
                          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Equip</div>
                          <span className={cn(
                            "inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold mt-0.5",
                            row.equipment === "Van" && "bg-info-subtle text-info",
                            row.equipment === "Reefer" && "bg-ai-subtle text-ai",
                            row.equipment === "Flatbed" && "bg-warning-subtle text-warning",
                            !["Van","Reefer","Flatbed"].includes(row.equipment) && "bg-muted text-muted-foreground"
                          )}>{row.equipment}</span>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Miles</div>
                          <div className="font-mono">{row.miles > 0 ? row.miles.toLocaleString() : '—'}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Rate</div>
                          <div className="font-mono font-semibold">{row.rate > 0 ? `$${row.rate.toLocaleString()}` : '—'}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">AI Rate</div>
                          {row.aiRate !== null ? (
                            <div className={cn(
                              "inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold mt-0.5",
                              row.aiDirection === "up" && "bg-brand-teal-deep text-white",
                              row.aiDirection === "down" && "text-danger bg-danger/10",
                              row.aiDirection === "neutral" && "bg-muted text-muted-foreground"
                            )}>
                              {row.aiDirection === "up" && <ArrowUpRight className="h-2.5 w-2.5" />}
                              {row.aiDirection === "down" && <ArrowDownRight className="h-2.5 w-2.5" />}
                              {row.aiDirection === "neutral" && <Minus className="h-2.5 w-2.5" />}
                              ${row.aiRate.toLocaleString()}
                            </div>
                          ) : <div className="text-muted-foreground">—</div>}
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Mkt RPM</div>
                          <div className="font-mono">{row.marketRpm > 0 ? `$${row.marketRpm.toFixed(2)}` : '—'}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Views</div>
                          {(viewCounts[row.id] ?? 0) > 0 ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setViewerLoadId(row.id); }}
                              className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-accent hover:underline"
                            >
                              <Eye className="h-3 w-3" />{viewCounts[row.id]}
                            </button>
                          ) : <div className="text-muted-foreground">—</div>}
                        </div>
                      </div>
                    </button>

                    {/* Action row */}
                    <div className="flex items-center gap-1 border-t bg-muted/20 px-2 py-1.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(row.id); }}
                        className="flex h-7 w-7 items-center justify-center rounded hover:bg-muted text-muted-foreground"
                        title="Edit"
                      ><Edit2 className="h-3.5 w-3.5" /></button>
                      {canBoost && (
                        <Popover open={boostingLoadId === row.id} onOpenChange={(open) => setBoostingLoadId(open ? row.id : null)}>
                          <PopoverTrigger asChild>
                            <button onClick={(e) => e.stopPropagation()} className="flex h-7 w-7 items-center justify-center rounded hover:bg-primary/10 text-primary" title="Boost">
                              <Zap className="h-3.5 w-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-52 p-2" align="end" onClick={(e) => e.stopPropagation()}>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Boost this load</p>
                            {(!wallet || wallet.balance <= 0) ? (
                              <div className="text-[11px] text-muted-foreground py-2 space-y-1.5">
                                <p className="font-semibold text-foreground">Insufficient credits</p>
                                <p>{isCompanyAdmin ? "Purchase more credits in Company Settings → Credits & Billing." : "Please contact your system administrator to purchase more credits."}</p>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {BOOST_TIERS.map(tier => (
                                  <Button key={tier.minutes} variant="ghost" size="sm" className="w-full justify-between text-[11px] h-7" onClick={() => handleBoost(row.id, tier.minutes, tier.credits)}>
                                    <span>{tier.minutes} min</span>
                                    <span className="font-mono text-muted-foreground">{tier.credits} cr</span>
                                  </Button>
                                ))}
                              </div>
                            )}
                            <p className="text-[9px] text-muted-foreground mt-1.5">Balance: {wallet?.balance ?? 0} credits</p>
                          </PopoverContent>
                        </Popover>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); toast({ title: "Coming soon", description: "Carrier Connect will be available in a future update." }); }}
                        className="flex h-7 w-7 items-center justify-center rounded hover:bg-muted text-muted-foreground"
                        title="Carrier Connect"
                      ><UserCheck className="h-3.5 w-3.5" /></button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRepost(row.id); }}
                        className="flex h-7 w-7 items-center justify-center rounded hover:bg-muted text-muted-foreground"
                        title="Repost"
                      ><RefreshCw className="h-3.5 w-3.5" /></button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDuplicate(row.id); }}
                        className="flex h-7 w-7 items-center justify-center rounded hover:bg-muted text-muted-foreground"
                        title="Duplicate"
                      ><Copy className="h-3.5 w-3.5" /></button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button onClick={(e) => e.stopPropagation()} className="ml-auto flex h-7 w-7 items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Remove">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-52 p-1" onClick={(e) => e.stopPropagation()}>
                          <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Remove reason</p>
                          {REMOVE_REASONS.map(r => (
                            <button key={r.value} onClick={() => handleRemoveWithReason(row.id, r.value)} className="flex w-full flex-col items-start rounded px-2 py-1.5 text-left hover:bg-muted">
                              <span className="text-sm font-medium text-foreground">{r.label}</span>
                              <span className="text-[10px] text-muted-foreground">{r.description}</span>
                            </button>
                          ))}
                        </PopoverContent>
                      </Popover>
                    </div>

                    {isExpanded && (
                      <div className="border-t">
                        <LoadDetailsPanel load={detailRow} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto flex-1">
              <table className="w-full text-caption">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <SortableTH label="REF #" headerKey="ref" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="Status" headerKey="status" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="Origin" headerKey="origin" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="Destination" headerKey="dest" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="Miles" headerKey="miles" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="Equip." headerKey="equip" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="Rate" headerKey="rate" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="AI Suggested Rate" headerKey="ai_rate" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="Mkt RPM" headerKey="mkt_rpm" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="Age" headerKey="age" sortBy={sortBy} onSort={setSortBy} />
                    <SortableTH label="Views" headerKey="views" sortBy={sortBy} onSort={setSortBy} />
                    <th className="px-3 py-1.5 text-left text-[10px] font-bold uppercase tracking-wide text-muted-foreground whitespace-nowrap"></th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map((row, i) => {
                    const isExpanded = expandedLoadId === row.id;
                    const statusStyle = STATUS_STYLES[row.status];
                    const hasBoosted = !!activeBoosts[row.id];
                    const canBoost = row.status !== 'expired' && !hasBoosted;
                    const detailRow = {
                      id: i, loadId: row.id, ageMinutes: row.ageHours * 60,
                      pickupDate: row.pickupDate, dropDate: row.deliveryDate,
                      originCity: row.origin.split(', ')[0] || '', originState: row.originState,
                      destCity: row.destination.split(', ')[0] || '', destState: row.destinationState,
                      pickups: 1, drops: 1, type: 'FTL' as const,
                      rate: row.rate, weight: row.weightLbs, miles: row.miles, equipment: row.equipment,
                      ratePerMile: row.miles > 0 ? Math.round((row.rate / row.miles) * 100) / 100 : 0,
                      company: '', verified: false, createdBy: row.createdBy, contactName: row.contactName,
                      lengthFt: row.lengthFt, commodity: row.commodity, paymentTerms: row.paymentTerms,
                      specialInstructions: row.specialInstructions, referenceNumber: row.referenceNumber,
                      loadNumber: row.loadNumber, pieces: row.pieces,
                      briefAudioPath: row.briefAudioPath, briefDurationSeconds: row.briefDurationSeconds,
                    };
                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          onClick={() => setExpandedLoadId(prev => prev === row.id ? null : row.id)}
                          className={cn(
                            "border-b transition-colors hover:bg-muted/40 cursor-pointer",
                            i % 2 === 0 ? "bg-card" : "bg-muted/10",
                            isExpanded && "border-l-2 border-l-accent bg-muted/30"
                          )}
                        >
                          <td className="px-3 py-1.5 font-mono font-semibold text-foreground whitespace-nowrap text-[11px]">
                            <span className="flex items-center gap-1">
                              {row.ref}
                              {activeBoosts[row.id] && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-brand-teal bg-brand-teal/10 rounded-full px-1.5 py-0.5">
                                  <Zap className="h-2.5 w-2.5" />
                                  {Math.max(0, Math.ceil((new Date(activeBoosts[row.id]).getTime() - Date.now()) / 60000))}m
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-3 py-1.5">
                            <span className={cn("rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide", statusStyle.className)}>
                              {statusStyle.label}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-foreground whitespace-nowrap text-[11px]">{row.origin}</td>
                          <td className="px-3 py-1.5 text-foreground whitespace-nowrap text-[11px]">{row.destination}</td>
                          <td className="px-3 py-1.5 text-muted-foreground font-mono text-[11px]">{row.miles > 0 ? row.miles.toLocaleString() : '—'}</td>
                          <td className="px-3 py-1.5">
                            <span className={cn(
                              "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                              row.equipment === "Van"     && "bg-info-subtle text-info",
                              row.equipment === "Reefer"  && "bg-ai-subtle text-ai",
                              row.equipment === "Flatbed" && "bg-warning-subtle text-warning",
                              !["Van","Reefer","Flatbed"].includes(row.equipment) && "bg-muted text-muted-foreground"
                            )}>
                              {row.equipment}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 font-mono font-semibold text-foreground text-[11px]">
                            {row.rate > 0 ? `$${row.rate.toLocaleString()}` : '—'}
                          </td>
                          <td className="px-3 py-1.5">
                            {row.aiRate !== null ? (
                              <div className="flex flex-col gap-0.5">
                                {row.aiDirection === "up" && (
                                  <span className="inline-flex items-center gap-1 rounded-md px-2.5 py-0.5 text-[11px] font-bold"
                                    style={{ backgroundColor: "hsl(var(--brand-teal-deep))", color: "#ffffff" }}
                                  >
                                    <ArrowUpRight className="h-3 w-3" />${row.aiRate.toLocaleString()}
                                  </span>
                                )}
                                {row.aiDirection === "down" && (
                                  <span className="inline-flex items-center gap-1 rounded-md px-2.5 py-0.5 text-[11px] font-bold"
                                    style={{ backgroundColor: "hsl(var(--danger) / 0.12)", color: "hsl(var(--danger))" }}
                                  >
                                    <ArrowDownRight className="h-3 w-3" />${row.aiRate.toLocaleString()}
                                  </span>
                                )}
                                {row.aiDirection === "neutral" && (
                                  <span className="inline-flex items-center gap-1 rounded-md px-2.5 py-0.5 text-[11px] font-bold"
                                    style={{ backgroundColor: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))" }}
                                  >
                                    <Minus className="h-3 w-3" />${row.aiRate.toLocaleString()}
                                  </span>
                                )}
                                {row.marketTrendDirection && row.marketTrendPercent !== null ? (
                                  <span className={cn(
                                    "text-[9px] font-medium pl-1",
                                    row.marketTrendDirection === 'up' && "text-primary",
                                    row.marketTrendDirection === 'down' && "text-destructive",
                                    row.marketTrendDirection === 'neutral' && "text-muted-foreground",
                                  )}>
                                    {row.marketTrendDirection === 'up' && `↑ ${Math.abs(row.marketTrendPercent)}% today`}
                                    {row.marketTrendDirection === 'down' && `↓ ${Math.abs(row.marketTrendPercent)}% today`}
                                    {row.marketTrendDirection === 'neutral' && `→ Flat today`}
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-[11px]">—</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 font-mono text-muted-foreground text-[11px]">
                            {row.marketRpm > 0 ? `$${row.marketRpm.toFixed(2)}` : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap text-[11px]">{ageLabel(row.ageHours)}</td>
                          <td className="px-3 py-1.5 text-center">
                            {(viewCounts[row.id] ?? 0) > 0 ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); setViewerLoadId(row.id); }}
                                className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-accent hover:underline cursor-pointer"
                                title="View carrier details"
                              >
                                <Eye className="h-3 w-3" />
                                {viewCounts[row.id]}
                              </button>
                            ) : (
                              <span className="text-[11px] text-muted-foreground/50">—</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleEdit(row.id); }}
                                className="flex h-5 w-5 items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="h-2.5 w-2.5" />
                              </button>
                              {canBoost && (
                                <Popover open={boostingLoadId === row.id} onOpenChange={(open) => setBoostingLoadId(open ? row.id : null)}>
                                  <PopoverTrigger asChild>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); }}
                                      className="flex h-5 w-5 items-center justify-center rounded hover:bg-primary/10 text-primary hover:text-primary transition-colors"
                                      title="Boost load"
                                    >
                                      <Zap className="h-2.5 w-2.5" />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-52 p-2" align="end" onClick={(e) => e.stopPropagation()}>
                                    <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Boost this load</p>
                                    {(!wallet || wallet.balance <= 0) ? (
                                      <div className="text-[11px] text-muted-foreground py-2 space-y-1.5">
                                        <p className="font-semibold text-foreground">Insufficient credits</p>
                                        <p>
                                          {isCompanyAdmin
                                            ? "Purchase more credits in Company Settings → Credits & Billing."
                                            : "Please contact your system administrator to purchase more credits."}
                                        </p>
                                      </div>
                                    ) : (
                                      <div className="space-y-1">
                                        {BOOST_TIERS.map(tier => (
                                          <Button
                                            key={tier.minutes}
                                            variant="ghost"
                                            size="sm"
                                            className="w-full justify-between text-[11px] h-7"
                                            onClick={() => handleBoost(row.id, tier.minutes, tier.credits)}
                                          >
                                            <span>{tier.minutes} min</span>
                                            <span className="font-mono text-muted-foreground">{tier.credits} cr</span>
                                          </Button>
                                        ))}
                                      </div>
                                    )}
                                    <p className="text-[9px] text-muted-foreground mt-1.5">Balance: {wallet?.balance ?? 0} credits</p>
                                  </PopoverContent>
                                </Popover>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); toast({ title: "Coming soon", description: "Carrier Connect will be available in a future update." }); }}
                                className="flex h-5 w-5 items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                title="Carrier Connect"
                              >
                                <UserCheck className="h-2.5 w-2.5" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRepost(row.id); }}
                                className="flex h-5 w-5 items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                title="Repost"
                              >
                                <RefreshCw className="h-2.5 w-2.5" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDuplicate(row.id); }}
                                className="flex h-5 w-5 items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                title="Duplicate Load"
                              >
                                <Copy className="h-2.5 w-2.5" />
                              </button>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); }}
                                    className="flex h-5 w-5 items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                    title="Remove Load"
                                  >
                                    <Trash2 className="h-2.5 w-2.5" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-52 p-1" onClick={(e) => e.stopPropagation()}>
                                  <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Remove reason</p>
                                  {REMOVE_REASONS.map(r => (
                                    <button
                                      key={r.value}
                                      onClick={() => { handleRemoveWithReason(row.id, r.value); }}
                                      className="flex w-full flex-col items-start rounded px-2 py-1.5 text-left hover:bg-muted transition-colors"
                                    >
                                      <span className="text-sm font-medium text-foreground">{r.label}</span>
                                      <span className="text-[10px] text-muted-foreground">{r.description}</span>
                                    </button>
                                  ))}
                                </PopoverContent>
                              </Popover>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${row.id}-detail`} className="border-b">
                            <td colSpan={12} className="p-0">
                              <LoadDetailsPanel load={detailRow} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="flex items-center justify-center border-t bg-muted/20 px-3 py-2">
          <div className="flex items-center gap-1.5 text-caption text-muted-foreground">
            <span className="text-[10px]">◎</span>
            <span>{displayed.length} loads</span>
            <span>·</span>
            <span className="text-[10px]">Powered by ATLAS</span>
            {maxRows && (
              <>
                <span>·</span>
                <span className="text-[10px]">To see more loads, go to <Link to="/my-loads" className="underline text-accent hover:text-accent/80 transition-colors">My Loads</Link></span>
              </>
            )}
          </div>
        </div>
      </div>

      {viewerLoadId && (
        <LoadViewersDialog
          loadId={viewerLoadId}
          loadRef={viewerLoad?.ref ?? ''}
          open={!!viewerLoadId}
          onOpenChange={(open) => { if (!open) setViewerLoadId(null); }}
        />
      )}

      {/* Remove reason is now handled inline via Popover — no AlertDialog needed */}
    </>
  );
}
